import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Icon,
  Box,
  useToast,
  Link,
} from '@chakra-ui/react';
import { FaBell, FaEnvelope, FaStar } from 'react-icons/fa';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';
import { subscribeToLeadMagnet } from '../../brain/api';
import BlogTrackAlternatives from './BlogTrackAlternatives.jsx';
import {
  getBlogOuting,
  getBlogTrackAlternatives,
  getOutingLink,
} from './blogOutingMaps.js';

const POPUP_DELAY = 5000; // 5 seconds before activation
const SCROLL_THRESHOLD = 70; // 70% scroll threshold
const STORAGE_KEY = 'emailCaptureShown';
const STORAGE_EXPIRY_DAYS = 30; // Show popup again after 30 days

const TESTIMONIALS_PAGE = 'https://reservenature.com/testimonials';

// Check if popup was shown within the expiry period
const hasShownRecently = () => {
  const storedTime = localStorage.getItem(STORAGE_KEY);
  if (!storedTime) return false;

  const shownDate = parseInt(storedTime, 10);
  const now = Date.now();
  const daysSinceShown = (now - shownDate) / (1000 * 60 * 60 * 24);

  return daysSinceShown < STORAGE_EXPIRY_DAYS;
};

export default function ExitIntentPopup({
  headline = 'The surest way to track cancellations',
  subhead =
    'Reserve Nature monitors sold-out permits and campgrounds around the clock—so you hear the moment a spot opens.',
  cta = 'Email me when spots open',
  source = 'cancellation-alerts',
}) {
  const location = useLocation();
  const toast = useToast();
  const { showExitPopup, toggleExitPopup, putEvent, user, testimonials, fetchTestimonials } =
    useUIStore(
      (state) => ({
        showExitPopup: state.showExitPopup,
        toggleExitPopup: state.toggleExitPopup,
        putEvent: state.putEvent,
        user: state.user,
        testimonials: state.testimonials,
        fetchTestimonials: state.fetchTestimonials,
      }),
      shallow
    );

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isActivated, setIsActivated] = useState(false);
  const [triggerSource, setTriggerSource] = useState(null);
  const hasTriggered = useRef(false);
  const scrollDepth = useRef(0);
  const testimonialsFetchStarted = useRef(false);

  // Extract page context from URL
  const getPageContext = useCallback(() => {
    const pathname = location.pathname;
    let pageSlug = null;
    let pageType = 'other';

    if (pathname.startsWith('/blogs/')) {
      pageSlug = pathname.replace('/blogs/', '').replace(/\/$/, '');
      pageType = 'blog';
    } else if (pathname.startsWith('/campground/')) {
      pageSlug = pathname.replace('/campground/', '').replace(/\/$/, '');
      pageType = 'outing';
    } else if (pathname.startsWith('/permit/')) {
      pageSlug = pathname.replace('/permit/', '').replace(/\/$/, '');
      pageType = 'outing';
    } else if (pathname.startsWith('/state/')) {
      pageSlug = pathname.replace('/state/', '').replace(/\/$/, '');
      pageType = 'state';
    } else if (pathname.startsWith('/country/')) {
      pageSlug = pathname.replace('/country/', '').replace(/\/$/, '');
      pageType = 'country';
    } else if (pathname === '/') {
      pageType = 'landing';
    }

    return {
      pageSlug,
      pageType,
      pageUrl: window.location.href,
    };
  }, [location.pathname]);

  // Only show popup on blog pages for non-logged-in users
  const shouldShowPopup = useCallback(() => {
    // Don't show for logged-in users
    if (user) return false;

    // Only show on blog pages
    if (!location.pathname.startsWith('/blogs/')) return false;

    const excludedPaths = ['/download/', '/checkout', '/profile', '/admin'];
    return !excludedPaths.some((path) => location.pathname.includes(path));
  }, [location.pathname, user]);

  // Trigger popup with tracking
  const triggerPopup = useCallback(
    (triggerType) => {
      if (hasTriggered.current || !isActivated || !shouldShowPopup()) return;

      hasTriggered.current = true;
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      setTriggerSource(triggerType);
      toggleExitPopup(true);

      const ctx = getPageContext();
      putEvent?.({
        type: 'email-capture-shown',
        source: triggerType,
        scrollDepth: scrollDepth.current,
        ...ctx,
      });
    },
    [isActivated, toggleExitPopup, putEvent, shouldShowPopup, getPageContext]
  );

  // Desktop: Exit intent detection
  useEffect(() => {
    if (hasShownRecently() || !shouldShowPopup()) return;

    const handleMouseLeave = (e) => {
      // Trigger if mouse leaves from top (Y < 10)
      if (e.clientY <= 10 && isActivated && !hasTriggered.current) {
        triggerPopup('exit-intent-desktop');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [isActivated, triggerPopup, shouldShowPopup]);

  // Scroll-based trigger (both mobile and desktop)
  useEffect(() => {
    if (hasShownRecently() || !shouldShowPopup()) return;

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

      scrollDepth.current = Math.max(
        scrollDepth.current,
        Math.round(scrollPercent)
      );

      // Trigger when scrolled past 70%
      if (
        scrollPercent >= SCROLL_THRESHOLD &&
        isActivated &&
        !hasTriggered.current
      ) {
        triggerPopup('scroll-70');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isActivated, triggerPopup, shouldShowPopup]);

  // Activation timer - starts after page fully loads
  useEffect(() => {
    if (hasShownRecently() || !shouldShowPopup()) return;

    const startTimer = () => {
      const timer = setTimeout(() => {
        setIsActivated(true);
      }, POPUP_DELAY);
      return timer;
    };

    let timer;

    if (document.readyState === 'complete') {
      timer = startTimer();
    } else {
      const handleLoad = () => {
        timer = startTimer();
      };
      window.addEventListener('load', handleLoad);
      return () => {
        window.removeEventListener('load', handleLoad);
        if (timer) clearTimeout(timer);
      };
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [shouldShowPopup]);

  useEffect(() => {
    if (
      user ||
      !location.pathname.startsWith('/blogs/') ||
      testimonialsFetchStarted.current
    ) {
      return;
    }
    testimonialsFetchStarted.current = true;
    fetchTestimonials?.();
  }, [user, location.pathname, fetchTestimonials]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const pageContext = getPageContext();

      await subscribeToLeadMagnet({
        email: trimmedEmail,
        source: triggerSource || 'manual',
        pageSlug: pageContext.pageSlug,
        pageType: pageContext.pageType,
        pageUrl: pageContext.pageUrl,
        metadata: {
          scrollDepth: scrollDepth.current,
        },
      });

      putEvent?.({
        type: 'email-capture-subscribed',
        source: triggerSource,
        email: trimmedEmail,
        scrollDepth: scrollDepth.current,
        ...pageContext,
      });

      toggleExitPopup(false);

      toast({
        title: "You're in!",
        description: "We'll notify you when spots open up.",
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error subscribing:', error);

      const errorMessage =
        error?.response?.data?.failure?.message ||
        error?.data?.failure?.message ||
        'Something went wrong. Please try again.';

      setEmailError(errorMessage);

      putEvent?.({
        type: 'email-capture-failed',
        source: triggerSource,
        error: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    toggleExitPopup(false);
    putEvent?.({
      type: 'email-capture-closed',
      scrollDepth: scrollDepth.current,
    });
  };

  const exitModalTestimonials = useMemo(
    () => (Array.isArray(testimonials) ? testimonials.slice(0, 2) : []),
    [testimonials]
  );

  const pageSlug = location.pathname.startsWith('/blogs/')
    ? location.pathname.replace('/blogs/', '').replace(/\/$/, '')
    : null;
  const trackAlternatives = pageSlug
    ? getBlogTrackAlternatives(pageSlug)
    : null;
  const blogOuting = pageSlug ? getBlogOuting(pageSlug) : null;
  const modalHeadline = trackAlternatives?.headline ?? headline;
  const modalSubhead = trackAlternatives?.subhead ?? subhead;
  const showEmailCapture = !trackAlternatives;

  return (
    <Modal
      isOpen={showExitPopup}
      onClose={handleClose}
      size={{ base: 'sm', md: '2xl' }}
      isCentered
      closeOnOverlayClick={true}
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent mx={4} borderRadius="2xl" overflow="hidden" boxShadow="2xl">
        <ModalCloseButton
          top={3}
          right={3}
          size="sm"
          color="gray.500"
          _hover={{ bg: 'gray.100' }}
          borderRadius="full"
          zIndex={10}
        />

        <ModalBody p={0}>
          <VStack spacing={4} p={6} align="stretch">
            <Heading
              size={{ base: 'md', md: 'lg' }}
              textAlign="center"
              color="gray.800"
              fontWeight="bold"
              lineHeight="short"
            >
              {modalHeadline}
            </Heading>

            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              color="gray.600"
              textAlign="center"
              lineHeight="tall"
            >
              {modalSubhead}
            </Text>

            {blogOuting && !trackAlternatives && (
              <Button
                as={RouterLink}
                to={getOutingLink(blogOuting)}
                onClick={handleClose}
                size="lg"
                width="full"
                leftIcon={<FaBell />}
                colorScheme="brand"
              >
                Track {blogOuting.name}
              </Button>
            )}

            {trackAlternatives && pageSlug && (
              <BlogTrackAlternatives
                blogSlug={pageSlug}
                onSelect={handleClose}
              />
            )}

            {exitModalTestimonials.length > 0 && showEmailCapture && (
              <VStack spacing={3} align="stretch" w="full">
                {exitModalTestimonials.map((t, idx) => {
                  const quote = t.description || t.text || '';
                  const who = t.name || 'Verified customer';
                  if (!quote) return null;
                  return (
                    <Box
                      key={t._id || idx}
                      bg="green.50"
                      borderWidth="1px"
                      borderColor="green.100"
                      borderRadius="lg"
                      px={4}
                      py={3}
                      w="full"
                    >
                      <HStack spacing={1} mb={2} justify="center">
                        {[...Array(5)].map((_, i) => (
                          <Icon key={i} as={FaStar} color="amber.400" boxSize={3} />
                        ))}
                      </HStack>
                      <Text
                        fontSize="sm"
                        color="gray.700"
                        fontStyle="italic"
                        lineHeight="tall"
                        textAlign="center"
                        noOfLines={5}
                      >
                        &ldquo;{quote}&rdquo;
                      </Text>
                      <Text
                        fontSize="xs"
                        color="gray.600"
                        mt={2}
                        textAlign="center"
                        fontWeight="medium"
                      >
                        {who}
                      </Text>
                    </Box>
                  );
                })}
                <Link
                  as={RouterLink}
                  to="/testimonials"
                  fontSize="sm"
                  color="brand.600"
                  fontWeight="semibold"
                  textAlign="center"
                  _hover={{ color: 'brand.700', textDecoration: 'underline' }}
                >
                  More reviews on reservenature.com/testimonials
                </Link>
              </VStack>
            )}

            {showEmailCapture && (
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <VStack spacing={3}>
                  <FormControl isInvalid={!!emailError}>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError('');
                      }}
                      size="lg"
                      bg="gray.50"
                      borderWidth="2px"
                      borderColor={emailError ? 'red.300' : 'gray.200'}
                      _hover={{ borderColor: 'brand.300' }}
                      _focus={{
                        borderColor: 'brand.500',
                        boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                      }}
                      _placeholder={{ color: 'gray.400' }}
                      fontSize="md"
                      borderRadius="lg"
                    />
                    {emailError && (
                      <FormErrorMessage fontSize="sm">{emailError}</FormErrorMessage>
                    )}
                  </FormControl>

                  <Button
                    type="submit"
                    size="lg"
                    width="full"
                    isLoading={isSubmitting}
                    loadingText="Subscribing..."
                    leftIcon={<FaEnvelope />}
                    fontWeight="bold"
                    fontSize="md"
                    bg="secondary.500"
                    color="white"
                    _hover={{
                      bg: 'secondary.600',
                      transform: 'translateY(-1px)',
                      boxShadow: 'lg',
                    }}
                    _active={{
                      transform: 'translateY(0px)',
                    }}
                    transition="all 0.2s"
                    borderRadius="lg"
                  >
                    {cta}
                  </Button>
                </VStack>
              </form>
            )}

            {showEmailCapture && (
              <Text fontSize="xs" color="gray.400" textAlign="center">
                Free alerts when reservations cancel. No spam—unsubscribe anytime. Reviews:{' '}
                <Link href={TESTIMONIALS_PAGE} isExternal color="brand.500" fontWeight="medium">
                  reservenature.com/testimonials
                </Link>
              </Text>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
